import { computed, inject } from 'vue';
import { Store } from 'vuex';
import { AppState } from '@/store';
import {
  Bell,
  Building,
  Building2Icon,
  BarChart3Icon,
  Boxes,
  BrickWall,
  ChartColumn,
  ChartColumnStacked,
  ChartLine,
  ChartNoAxesCombined,
  ClipboardList,
  Database,
  Factory,
  FileCheck,
  FileTextIcon,
  FileQuestionIcon,
  Globe,
  History,
  House,
  KeyRound,
  Layers,
  Loader,
  MessageSquare,
  NotebookText,
  Palette,
  Percent,
  Search,
  SettingsIcon,
  SquarePen,
  Star,
  Table,
  UsersIcon,
  UserIcon,
} from '@lucide/vue';
import {
  CanViewNotifications,
  IsUserAnAdmin,
  IsUserAClient,
  IsMarketUser,
  GetAllowedCarrierIds,
  GetAllowedWholesalerIds,
} from '@/utilities/AuthUtils';
import { colors } from '@/utilities/Color';
import type { Component } from 'vue';

export interface NavigationLink {
  title: string;
  to?: string;
  icon: Component;
  action?: string;
  hidden?: boolean;
  badge?: string;
}

export interface NavigationGroup {
  groupId: string;
  name: string;
  description: string;
  icon: Component;
  color: string;
  iconColor: string;
  hoverColor: string;
  isAdmin?: boolean;
  links: NavigationLink[];
}

export const iconMap: Record<string, Component> = {
  Bell,
  Building,
  Building2Icon,
  BarChart3Icon,
  Boxes,
  BrickWall,
  ChartColumn,
  ChartColumnStacked,
  ChartLine,
  ChartNoAxesCombined,
  ClipboardList,
  Database,
  Factory,
  FileCheck,
  FileTextIcon,
  FileQuestionIcon,
  Globe,
  History,
  House,
  KeyRound,
  Layers,
  Loader,
  MessageSquare,
  NotebookText,
  Palette,
  Percent,
  Search,
  SettingsIcon,
  SquarePen,
  Star,
  Table,
  UsersIcon,
  UserIcon,
};

export function getIconName(icon: Component): string {
  return Object.entries(iconMap).find(([, comp]) => comp === icon)?.[0] ?? '';
}

export function resolveIcon(iconName: string): Component | undefined {
  return iconMap[iconName];
}

export interface FlatNavigationLink {
  id: string;
  title: string;
  to?: string;
  action?: string;
  iconName: string;
  groupId: string;
  groupName: string;
  groupColor: string;
}

export function useNavigationLinks() {
  const store = inject<Store<AppState>>('store');

  const isAdmin = computed(() => IsUserAnAdmin(store?.state.user));
  const canViewNotifications = computed(() => CanViewNotifications(store?.state.user));

  const navigationGroups = computed<NavigationGroup[]>(() =>
    [
      {
        groupId: 'clients',
        name: 'Clients',
        description: 'Comprehensive client tools for portfolio management',
        icon: UsersIcon,
        color: colors.medium_slate,
        iconColor: colors.medium_slate,
        hoverColor: colors.medium_slate,
        links: [
          { title: 'Client Management', to: '/accounts', icon: Building },
          {
            title: 'Analytics Hub',
            to: '/analytics',
            icon: ChartColumn,
            hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
          },
          {
            title: 'Towers',
            to: '/v2/tower',
            icon: ChartColumnStacked,
            hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
          },
          {
            title: 'AI Data Extraction Results',
            to: '/ai-results',
            icon: FileCheck,
            hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
          },
        ],
      },
      {
        groupId: 'book-of-business',
        name: 'Book of Business',
        description: 'Advanced policy exploration',
        icon: FileTextIcon,
        color: colors.light_gray,
        iconColor: colors.light_gray,
        hoverColor: colors.light_gray,
        links: [
          {
            title: 'Home',
            to: '/user/home',
            icon: House,
            badge: 'New',
            hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
          },
          {
            title: 'Policy Explorer',
            to: '/policy/explorer',
            icon: Search,
            hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
          },
        ],
      },
      {
        groupId: 'marketplace',
        name: 'Marketplace',
        description: 'Carrier performance analytics, wholesaler distribution insights, and market intelligence',
        icon: Building2Icon,
        color: colors.pale_blue,
        iconColor: colors.pale_blue,
        hoverColor: colors.pale_blue,
        links: [
          {
            title: 'Carrier Analytics',
            to: '/carriers',
            icon: Table,
            hidden: IsUserAClient(store?.state.user) || (IsMarketUser(store?.state.user) && GetAllowedCarrierIds(store?.state.user).length === 0),
          },
          {
            title: 'Wholesaler Analytics',
            to: '/wholesaler-group',
            icon: BrickWall,
            hidden: IsUserAClient(store?.state.user) || (IsMarketUser(store?.state.user) && GetAllowedWholesalerIds(store?.state.user).length === 0),
          },
          {
            title: 'Carrier Survey Results',
            to: '/carriers/surveys',
            icon: ClipboardList,
            hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
          },
          {
            title: 'AM Best Ratings',
            to: '/am-best',
            icon: Star,
            hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
          },
        ],
      },
      {
        groupId: 'management',
        name: 'Management',
        description: 'Financial analytics, yield analysis, and comprehensive business reporting',
        icon: BarChart3Icon,
        color: colors.specialty_teal,
        iconColor: colors.specialty_teal,
        hoverColor: colors.specialty_teal,
        links: [
          {
            title: 'Management Reporting',
            to: '/management-analytics',
            icon: ChartLine,
            hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
          },
          {
            title: 'Reporting Hub',
            to: '/reporting',
            icon: Factory,
            hidden: IsUserAClient(store?.state.user) || IsMarketUser(store?.state.user),
          },
          {
            title: 'Revenue Dashboard',
            to: '/revenue-dashboard',
            icon: ChartNoAxesCombined,
            hidden: !IsUserAnAdmin(store?.state.user),
          },
          {
            title: 'Yield Analysis',
            to: '/yield-analysis',
            icon: Percent,
            hidden: !IsUserAnAdmin(store?.state.user),
          },
        ],
      },
      {
        groupId: 'user-system',
        name: 'User & System',
        description: 'Manage your account settings and access support resources',
        icon: UserIcon,
        color: colors.dark_slate,
        iconColor: colors.dark_slate,
        hoverColor: colors.dark_slate,
        links: [
          ...(canViewNotifications.value
            ? [
                { title: 'Notifications', to: '/notifications', icon: Bell },
                { title: 'Notification Settings', to: '/notifications/preferences', icon: SettingsIcon },
              ]
            : []),
          { title: 'Demo Environment', action: 'demo', icon: Globe },
          { title: 'Submit Feedback', action: 'feedback', icon: MessageSquare },
          { title: 'Help Center', action: 'help', icon: FileQuestionIcon },
        ],
      },
      ...(isAdmin.value
        ? [
            {
              groupId: 'admin',
              name: 'Admin',
              description: 'System administration and configuration tools',
              icon: SettingsIcon,
              color: colors.agency_red,
              iconColor: colors.agency_red,
              hoverColor: colors.agency_red,
              isAdmin: true,
              links: [
                { title: 'Carrier Group Mapping', to: '/admin/carrier-issuer', icon: NotebookText },
                { title: 'Policy Type Mapping', to: '/admin/policy-type', icon: SquarePen },
                { title: 'Wholesaler Mapping', to: '/admin/wholesaler', icon: Boxes },
                { title: 'Sublimit / Subretention Types', to: '/admin/sublimit-types', icon: Layers },
                { title: 'Data Quality', to: '/admin/data-quality', icon: Database },
                { title: 'User Management', to: '/admin/users', icon: KeyRound },
                { title: 'AI Data Extraction', to: '/admin/ai', icon: Loader },
                { title: 'Reporting', to: '/admin/reporting', icon: ChartColumn },
                ...(import.meta.env.MODE === 'development'
                  ? [{ title: 'Custom Components', to: '/admin/component-showcase', icon: Palette }]
                  : []),
                { title: 'Advanced Settings', to: '/admin/advanced-settings', icon: SettingsIcon },
              ],
            },
          ]
        : []),
    ].filter((group) => group.links.length > 0),
  );

  const flatLinks = computed<FlatNavigationLink[]>(() => {
    const result: FlatNavigationLink[] = [];
    for (const group of navigationGroups.value) {
      for (const link of group.links) {
        if (link.hidden) continue;
        const id = link.to ?? (link.action ? `action:${link.action}` : link.title);
        result.push({
          id,
          title: link.title,
          to: link.to,
          action: link.action,
          iconName: getIconName(link.icon),
          groupId: group.groupId,
          groupName: group.name,
          groupColor: group.color,
        });
      }
    }
    return result;
  });

  return { navigationGroups, flatLinks };
}
